import { z } from 'zod';
import { PartnerType, PartnerStatus } from '../enums/partnerType';
import { BooksIntegrationProvider } from '../enums/booksIntegrationProvider';
import {
  billResponseSchema,
  generateBillsByOrgResponseSchema,
  slabPricingConfigSchema,
} from './admin';

// ============================================
// Partner Schemas
// ============================================

export const partnerSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(PartnerType),
  hostname: z.string().nullable().optional(),
  status: z.nativeEnum(PartnerStatus),
  metadata: z.record(z.unknown()).nullable().optional(),
  supportEmail: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type Partner = z.infer<typeof partnerSchema>;

export const partnersResponseSchema = z.array(partnerSchema);

// Create partner request
export const createPartnerRequestSchema = z.object({
  name: z.string().min(1, 'Partner name is required'),
  type: z.nativeEnum(PartnerType),
  hostname: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type CreatePartnerRequest = z.infer<typeof createPartnerRequestSchema>;

// Update partner request
export const updatePartnerRequestSchema = z.object({
  name: z.string().min(1).optional(),
  hostname: z.string().nullable().optional(),
  status: z.nativeEnum(PartnerStatus).optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type UpdatePartnerRequest = z.infer<typeof updatePartnerRequestSchema>;

// ============================================
// Partner Admin Schemas
// ============================================

export const partnerAdminSchema = z.object({
  employeeId: z.number(),
  partnerId: z.number().optional(),
  employeeName: z.string().optional(),
  employeeEmail: z.string().optional(),
});
export type PartnerAdmin = z.infer<typeof partnerAdminSchema>;

export const partnerAdminsResponseSchema = z.array(partnerAdminSchema);

// Response for adding a partner admin
export const addPartnerAdminResponseSchema = z.object({
  employeeId: z.number(),
  partnerId: z.number(),
  employeeName: z.string().optional(),
  employeeEmail: z.string().optional(),
});

// Add partner admin request (by email)
export const addPartnerAdminRequestSchema = z.object({
  email: z
    .string()
    .email('Valid email is required')
    .transform(val => val.toLowerCase()),
});
export type AddPartnerAdminRequest = z.infer<
  typeof addPartnerAdminRequestSchema
>;

// ============================================
// Partner Dashboard Schemas (what partner admins see)
// ============================================

// Partner context returned from GET /partner/me
export const partnerContextSchema = z.object({
  partnerId: z.number(),
  partnerName: z.string(),
  partnerType: z.nativeEnum(PartnerType),
  /* Books-platform integration the partner opted into (null = none). Drives
     partner-only UI like the Odoo mapping columns; resolved server-side from
     partner.metadata — see backend resolveBooksIntegration. */
  booksIntegration: z.nativeEnum(BooksIntegrationProvider).nullable(),
});
export type PartnerContext = z.infer<typeof partnerContextSchema>;

// Org as seen by partner admin
export const partnerOrgSchema = z.object({
  id: z.number(),
  name: z.string(),
  hostname: z.string(),
  businessCount: z.number(),
  createdAt: z.union([z.string(), z.date()]),
  /* The partner's own customer pricing for this org (they set it from the
     partner dashboard). Never carries defaultBillType/defaultZohoId. */
  slabPricing: slabPricingConfigSchema.nullish(),
});
export type PartnerOrg = z.infer<typeof partnerOrgSchema>;

export const partnerOrgsResponseSchema = z.array(partnerOrgSchema);

// Business as seen by partner admin (scoped to an org)
export const partnerBusinessSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  bizWhatsappNumber: z.string().nullable().optional(),
  countryCode: z.number().nullable().optional(),
  signUpType: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  /* Present ONLY when the partner has a books integration (the backend omits
     them otherwise). Zoho ids are never sent to partners. */
  odooCustomerId: z.string().nullable().optional(),
  booksUsername: z.string().nullable().optional(),
});
export type PartnerBusiness = z.infer<typeof partnerBusinessSchema>;

export const partnerBusinessesResponseSchema = z.array(partnerBusinessSchema);

// Business row for the partner-wide flat Businesses tab (adds the owning org
// and the WhatsApp Business Account / phone number ids)
export const partnerBusinessWithOrgSchema = partnerBusinessSchema.extend({
  orgId: z.number(),
  orgName: z.string().nullable().optional(),
  businessAccountId: z.string().nullable().optional(),
  phoneNumberId: z.string().nullable().optional(),
});
export type PartnerBusinessWithOrg = z.infer<
  typeof partnerBusinessWithOrgSchema
>;

export const partnerAllBusinessesResponseSchema = z.array(
  partnerBusinessWithOrgSchema,
);

// ============================================
// Partner Billing Schemas
// ============================================

/* Bill row as exposed to a partner admin. Deliberately FLAT and .strict():
   no volumeSource/remarks (bill type stays invisible to partners) and no
   nested business/org entities (which carry Heltar-internal fields).
   validateResponse only validates — it does not strip — so strictness is the
   fail-closed leak guard. */
export const partnerBillSchema = z
  .object({
    id: z.string().uuid(),
    businessId: z.number(),
    businessName: z.string(),
    businessAccountId: z.string().nullable(),
    phoneNumber: z.string(),
    orgId: z.number().nullable(),
    orgName: z.string().nullable(),
    periodStart: z.string(),
    periodEnd: z.string(),
    billData: billResponseSchema,
    /* A ₹0 bill a FAILED run left behind rather than a real zero charge (a run
       skips genuine zero usage and saves nothing). The boolean crosses the
       partner boundary; the reason, which names our internal sources, does
       not — see isNotBilledBill. */
    notBilled: z.boolean(),
    createdAt: z.union([z.string(), z.date()]),
    updatedAt: z.union([z.string(), z.date()]),
  })
  .strict();
export type PartnerBill = z.infer<typeof partnerBillSchema>;

export const partnerBillsResponseSchema = z.object({
  bills: z.array(partnerBillSchema),
  total: z.number(),
  page: z.number().optional(),
  limit: z.number().optional(),
});
export type PartnerBillsResponse = z.infer<typeof partnerBillsResponseSchema>;

/* Org pricing update from the partner dashboard. STRICT single-field
   whitelist — partners must never be able to set defaultBillType,
   defaultZohoId, or anything else on the org. null clears the pricing. */
export const partnerOrgPricingUpdateRequestSchema = z
  .object({
    slabPricing: slabPricingConfigSchema.nullable(),
  })
  .strict();
export type PartnerOrgPricingUpdateRequest = z.infer<
  typeof partnerOrgPricingUpdateRequestSchema
>;

export const partnerOrgPricingResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  slabPricing: slabPricingConfigSchema.nullable(),
  updatedAt: z.union([z.string(), z.date()]),
});
export type PartnerOrgPricingResponse = z.infer<
  typeof partnerOrgPricingResponseSchema
>;

/* Generate-all response: one run per partner org (same whitelisted per-org
   shape the single-org endpoint exposes) plus the aggregate counts. Strict at
   the top level so a leaked internal field 500s at validateResponse instead
   of shipping to the partner dashboard. */
export const generatePartnerAllBillsResponseSchema = z
  .object({
    totalOrgs: z.number(),
    totalBusinesses: z.number(),
    successCount: z.number(),
    failedCount: z.number(),
    orgs: z.array(generateBillsByOrgResponseSchema),
  })
  .strict();
export type GeneratePartnerAllBillsResponse = z.infer<
  typeof generatePartnerAllBillsResponseSchema
>;

// Assign partner to org request (from admin dashboard)
export const assignPartnerToOrgRequestSchema = z.object({
  partnerId: z.number().nullable(),
});
export type AssignPartnerToOrgRequest = z.infer<
  typeof assignPartnerToOrgRequestSchema
>;

// ============================================
// Partner Support Email Config Schemas
// ============================================

// Update support email config request (from partner dashboard)
// supportEmail: Partner's support email used for sending emails on their behalf (e.g. password reset)
export const updateEmailConfigRequestSchema = z.object({
  supportEmail: z.string().email('Valid email is required'),
  supportEmailPassword: z.string().min(1, 'App password is required'),
});
export type UpdateEmailConfigRequest = z.infer<
  typeof updateEmailConfigRequestSchema
>;

// Support email config response (masked password)
export const emailConfigResponseSchema = z.object({
  supportEmail: z.string().nullable(),
  isConfigured: z.boolean(),
});
export type EmailConfigResponse = z.infer<typeof emailConfigResponseSchema>;

// ============================================
// Partner Billing API (machine-facing)
// ============================================

/* A MONTH, not a date range. Bills are stored under exact period boundaries
   whose end is the first of the NEXT month, and the query matches them by
   equality — so a hand-built `2026-09-01 → 2026-09-30` range silently matches
   nothing. Taking the month and expanding it with monthToPeriod removes the
   only way to get a wrong-but-successful answer out of this API. */
export const partnerMonthlyBillsQuerySchema = z
  .object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM'),
    orgId: z.string().regex(/^\d+$/, 'orgId must be numeric').optional(),
  })
  .strict();
export type PartnerMonthlyBillsQuery = z.infer<
  typeof partnerMonthlyBillsQuerySchema
>;

/* The bill as a partner's own systems see it.

   Deliberately NOT the stored bill: `billData` repeats the business and the
   period that the row already carries, and its `openAi*` names date from when
   OpenAI was the only model we billed for — prod bills now carry Gemini rows
   under that key. Handing the internal payload over would make a partner's
   integration depend on both, so the whole surface is mapped explicitly and
   `toPartnerBillResponse` is the only place the two vocabularies meet.

   Money is INR throughout except `totals.aiUsd`, which is the pre-conversion
   figure the `usdToInrRate` was applied to. */
export const partnerBillLineSchema = z
  .object({
    countryCode: z.number(),
    isoCode: z.string(),
    /* MARKETING | UTILITY | AUTHENTICATION | MARKETING_LITE | SERVICE.
       Absent on RCS, which is billed flat per message with no category. */
    category: z.string().nullable().optional(),
    volume: z.number(),
    unitPrice: z.number(),
    cost: z.number(),
  })
  .strict();
export type PartnerBillLine = z.infer<typeof partnerBillLineSchema>;

export const partnerAiUsageSchema = z
  .object({
    model: z.string().nullable(),
    promptTokens: z.number(),
    completionTokens: z.number(),
    cachedTokens: z.number(),
    totalTokens: z.number(),
    costUsd: z.number(),
  })
  .strict();
export type PartnerAiUsage = z.infer<typeof partnerAiUsageSchema>;

export const partnerBillResponseSchema = z
  .object({
    billId: z.string().uuid(),

    orgId: z.number().nullable(),
    orgName: z.string().nullable(),
    businessId: z.number(),
    businessName: z.string(),
    businessAccountId: z.string().nullable(),
    /* The business's customer id in Route Mobile's Odoo, for a partner that
       wants to line our bills up against their own CRM. Read live from the
       business rather than frozen into the bill: almost none are set yet, and
       when they are, every past bill should carry the new value without a
       backfill. null — never omitted, never '' — when the business has none,
       so the shape of a bill never varies row to row. */
    odooCustomerId: z.string().nullable(),
    whatsappNumber: z.string(),

    month: z.string(),
    periodStart: z.string(),
    /* The first day of the NEXT month. Named for what it is: the stored period
       is half-open, and a consumer reading a plain `periodEnd` as inclusive
       would bill a day that belongs to the following month. */
    periodEndExclusive: z.string(),
    /* How far into the period the figures reach. Re-sync when it moves: a later
       regeneration can still shift numbers as Meta's data settles. */
    computedAt: z.union([z.string(), z.date()]),

    currency: z.literal('INR'),
    messaging: z.array(partnerBillLineSchema),
    rcs: z.array(partnerBillLineSchema),
    aiUsage: z.array(partnerAiUsageSchema),
    totals: z
      .object({
        messaging: z.number(),
        rcs: z.number(),
        ai: z.number(),
        /** Pre-conversion AI spend, before `usdToInrRate`. */
        aiUsd: z.number(),
        bill: z.number(),
      })
      .strict(),
    usdToInrRate: z.number(),
  })
  .strict();
export type PartnerBillResponse = z.infer<typeof partnerBillResponseSchema>;

export const partnerMonthlyBillsResponseSchema = z.object({
  month: z.string(),
  bills: z.array(partnerBillResponseSchema),
  total: z.number(),
  /* Bills held for this month that are NOT finalised — a mid-month estimate or
     a failed run's placeholder — and so are withheld from `bills`. Non-zero
     means the month is still settling for some customers, which is why the
     response comes back as PARTIAL_CONTENT rather than OK.

     Deliberately a count of rows we hold, not a list of customers without one:
     a customer with no usage at all is never given a bill row, so a "who is
     missing" list would name them forever and the signal would mean nothing. */
  notFinalised: z.number(),
});
export type PartnerMonthlyBillsResponse = z.infer<
  typeof partnerMonthlyBillsResponseSchema
>;

// ============================================
// Partner API keys
// ============================================

/* What every partner key is stamped with. Not offered as a picker and not part
   of SCOPE_RESOURCES (the business-key surface): a partner key's reach is
   decided by its kind, and the scope is here so a future write surface can be
   granted separately rather than by widening what these keys already hold. */
export const PARTNER_API_SCOPES = ['partner:read'] as const;

/* No scope picker: a partner key grants exactly the partner-scoped read APIs,
   and the scope is stamped server-side. Nothing here names the partner — that
   comes from the authenticated partner context, so a key's tenancy cannot be
   chosen by the request that mints it. */
export const createPartnerApiKeyRequestSchema = z
  .object({
    name: z.string().min(1, 'name is required').max(100),
    expiresInDays: z.number().int().positive().max(3650).optional(),
  })
  .strict();
export type CreatePartnerApiKeyRequest = z.infer<
  typeof createPartnerApiKeyRequestSchema
>;

export const partnerApiKeySchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().nullable(),
    prefix: z.string().nullable(),
    createdAt: z.union([z.string(), z.date()]),
    lastUsedAt: z.union([z.string(), z.date()]).nullable(),
    expiresAt: z.union([z.string(), z.date()]).nullable(),
    revokedAt: z.union([z.string(), z.date()]).nullable(),
  })
  .strict();
export type PartnerApiKey = z.infer<typeof partnerApiKeySchema>;

/** Create response — carries the raw secret exactly once, never stored. */
export const createdPartnerApiKeySchema = partnerApiKeySchema.extend({
  key: z.string(),
});
export type CreatedPartnerApiKey = z.infer<typeof createdPartnerApiKeySchema>;

export const partnerApiKeysResponseSchema = z.array(partnerApiKeySchema);
